import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import type { LolerReportDetail, LolerDefectItem, LolerDefectCategory, LolerDefectLifecycle, RemedialQuoteInput, ExtractedDefect, ReportCompleteness, QuoteLiftInput } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
// Session AMF: the Condition Report trigger, on the pre-contract path it is most about.
import { conditionReportTrigger, type ConditionReportTrigger } from "../../lib/conditionReportTrigger";
import { ConditionReportPrompt } from "./ConditionReportPrompt";
import { Button, buttonClasses } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill, type PillTone } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";

const EMPTY = "-";
const CATEGORIES: { value: LolerDefectCategory; label: string }[] = [
  { value: "immediate", label: "Immediate danger" },
  { value: "timed", label: "Remedy by date" },
  { value: "advisory", label: "Advisory" },
];
// Plain-English labels for the stored enums (Session AEH, FIX-1 A5): the office should never read a
// raw "timed" or "in_progress" value on the card.
const CATEGORY_LABEL: Record<LolerDefectCategory, string> = {
  immediate: "Immediate danger",
  timed: "Remedy by date",
  advisory: "Advisory",
};
const LIFECYCLE: LolerDefectLifecycle[] = ["open", "quoted", "scheduled", "in_progress", "complete", "signed_off"];
const LIFECYCLE_LABEL: Record<LolerDefectLifecycle, string> = {
  open: "Open",
  quoted: "Quoted",
  scheduled: "Scheduled",
  in_progress: "In progress",
  complete: "Complete",
  signed_off: "Signed off",
};

function gbp(n: number | null): string {
  if (n == null) return EMPTY;
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// The tone of a treatment pill: covered is green, chargeable is amber, absorbed is the LOLER blue.
function treatmentTone(t: string | null): PillTone {
  if (t === "covered") return "green";
  if (t === "chargeable") return "amber";
  if (t === "absorbed") return "loler";
  return "grey";
}

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const btnSm = "rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 disabled:opacity-50";

export function LolerReportDetailPage() {
  const { id = "" } = useParams();
  const [report, setReport] = useState<LolerReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setReport(await api.loler.report(id));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Loader shape="panel" />;
  if (error || !report)
    return (
      <div>
        <Link to="/office/loler" className="text-sm text-ele-green hover:underline">&larr; Back to reports</Link>
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">Could not load the report.</p>
      </div>
    );

  return (
    <div>
      <ReportHeader report={report} onChange={setReport} reload={load} />
      {/* Session AMF: the Condition Report trigger, placed here rather than anywhere else on this
          page because the header directly above it carries "Create maintenance quote" -- this IS
          the moment the published rule is about, a pre-contract enquiry turning into a quote. It
          renders nothing unless the report names a lift on the register that genuinely has no
          documented routine maintenance on file. */}
      <ConditionReportTriggerForReport assetId={report.assetId} liftRef={report.ele} />
      <ItemsTable report={report} reload={load} />
      <AddDefect reportId={id} reload={load} />
      <AiExtract reportId={id} hasReportPdf={report.hasReportPdf} completeness={report.completeness} reload={load} />
    </div>
  );
}

// Does the lift this report names need a Full Condition Report before ELE quotes it? (Session AMF)
//
// This is the LOLER PRE-CONTRACT PATH the brief named, and it is the sharpest case for the whole
// rule: a LOLER report arriving for a lift ELE does not maintain is one of the commonest ways a
// new enquiry reaches the office, and ELE's own published Schedule of Fees says in terms that a
// LOLER thorough examination does NOT satisfy the routine-maintenance evidence this asks about.
// The two facts sitting side by side on one screen is exactly the confusion the prompt is here to
// settle.
//
// TWO READS, AND ONLY WHEN THERE IS A LIFT TO ASK ABOUT. A report with `assetId: null` is a
// genuine pre-contract enquiry with nothing on the register at all: there is no lift record to
// attach a survey to, and create_condition_report / POST /api/schedule/condition-report both
// REQUIRE one, so there is nothing to offer and nothing is rendered. That is a real limit of this
// placement, not an oversight -- see the handoff.
//
// Both reads are best-effort: a failure leaves the prompt unrendered rather than putting an error
// on a page whose actual job is the LOLER report. Not knowing is reported as silence, never as a
// confident "no report needed".
function ConditionReportTriggerForReport({ assetId, liftRef }: { assetId: string | null; liftRef: string | null }) {
  const [trigger, setTrigger] = useState<ConditionReportTrigger | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const evaluate = useCallback(() => {
    if (!assetId) {
      setTrigger(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [asset, history] = await Promise.all([api.assets.get(assetId), api.assets.serviceHistory(assetId, { limit: 5 })]);
        if (cancelled) return;
        setTrigger(
          conditionReportTrigger({
            assetClass: asset.assetClass,
            hasContract: asset.hasContract,
            retiredAt: asset.retiredAt,
            lastServicedAt: asset.lastServicedAt,
            jobs: history.items,
            jobsComplete: history.items.length >= history.total,
          }),
        );
      } catch {
        if (!cancelled) setTrigger(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  useEffect(() => evaluate(), [evaluate]);

  if (!assetId || !trigger) return null;
  return (
    <>
      <ConditionReportPrompt
        trigger={trigger}
        assetId={assetId}
        liftRef={liftRef}
        onRaised={(message) => {
          setNotice(message);
          evaluate();
        }}
      />
      {notice && (
        <p role="status" className="mb-4 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {notice}
        </p>
      )}
    </>
  );
}

// AI assist (optional enhancement): paste the report text, get a DRAFT defect list the
// office confirms by adding each item. Never auto-converts. Degrades when the AI is off.
function AiExtract({
  reportId,
  hasReportPdf,
  completeness,
  reload,
}: {
  reportId: string;
  hasReportPdf: boolean;
  completeness: ReportCompleteness;
  reload: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [drafts, setDrafts] = useState<ExtractedDefect[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // The extraction's completeness signal. This panel used to drop the whole header, so a
  // report the model read as an EXCERPT was silently treated as complete on this surface.
  const [partialWarn, setPartialWarn] = useState(false);

  // source "text" reads the textarea; "pdf" reads the report's uploaded Drive PDF.
  async function extract(source: "text" | "pdf") {
    setBusy(true);
    setMsg(null);
    setDrafts(null);
    setPartialWarn(false);
    try {
      const res = await api.loler.extract(reportId, source === "text" ? text : undefined);
      setDrafts(res.defects);
      setPartialWarn(res.header?.completeness === "partial" && completeness !== "partial");
      if (res.defects.length === 0) setMsg("No defects were found. Check the report or enter them manually.");
    } catch (e) {
      setMsg(e instanceof ApiError ? (e.message || e.code) : "The AI assistant could not read the report.");
    } finally {
      setBusy(false);
    }
  }

  async function addOne(i: number) {
    const d = drafts?.[i];
    if (!d) return;
    setBusy(true);
    try {
      await api.loler.addDefect(reportId, { description: d.description, category: d.category, deadline: d.deadline ?? null });
      setDrafts((arr) => {
        const next = arr ? arr.filter((_, j) => j !== i) : arr;
        // The warning speaks about "the defects below": once the last one is confirmed there
        // is no list left for it to describe.
        if (!next || next.length === 0) setPartialWarn(false);
        return next;
      });
      await reload();
    } catch (e) {
      setMsg(friendlyApiError(e, "Could not add the defect."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <button onClick={() => setOpen((o) => !o)} className="font-heading text-lg tracking-wide text-ele-blue hover:underline">
        {open ? "Hide AI assist" : "AI assist: extract defects from report text"}
      </button>
      {open ? (
        <div className="mt-3">
          <p className="mb-2 text-xs text-ele-body">Paste the report text. The assistant proposes a draft list; you confirm and add each item. Nothing is added automatically.</p>
          <textarea className={inputCls + " min-h-[120px]"} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the LOLER report text here..." />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button disabled={busy || !text.trim()} onClick={() => extract("text")} className="rounded-ele bg-ele-blue px-4 py-2 font-heading text-xs tracking-wide text-ele-bg disabled:opacity-50">
              {busy ? "Reading..." : "Extract from text"}
            </button>
            {hasReportPdf ? (
              <button disabled={busy} onClick={() => extract("pdf")} className="rounded-ele border border-ele-blue px-4 py-2 font-heading text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue/10 disabled:opacity-50">
                {busy ? "Reading..." : "Extract from the uploaded PDF"}
              </button>
            ) : null}
            {msg ? <span className="text-xs text-amber-400">{msg}</span> : null}
          </div>
          {partialWarn ? (
            <p role="alert" className="mt-2 rounded-ele border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-400">
              This looks like a partial extract rather than the full report, so the defects below may be incomplete. Ask the duty holder for the
              complete document, and mark this report partial above so a remedial quote cannot be finalised on it by mistake.
            </p>
          ) : null}
          {drafts && drafts.length > 0 ? (
            <div className="mt-3 space-y-2">
              <p className="font-menu text-xs uppercase tracking-wide text-ele-body">Draft defects (confirm to add)</p>
              {drafts.map((d, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_140px_150px_auto]">
                  <input className={inputCls} value={d.description} onChange={(e) => setDrafts((arr) => (arr ? arr.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) : arr))} />
                  <select className={inputCls} value={d.category} onChange={(e) => setDrafts((arr) => (arr ? arr.map((x, j) => (j === i ? { ...x, category: e.target.value as LolerDefectCategory } : x)) : arr))}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  {/* Remedy by. Set it before confirming: a created defect has no deadline
                      editor on this page, and an undated item is never chased. */}
                  <input
                    type="date"
                    aria-label={"Remedy by date for defect " + (i + 1)}
                    className={inputCls}
                    value={d.deadline ?? ""}
                    onChange={(e) => setDrafts((arr) => (arr ? arr.map((x, j) => (j === i ? { ...x, deadline: e.target.value || undefined } : x)) : arr))}
                  />
                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => addOne(i)}>Add</Button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// Delete the report (admin only; the route enforces this too). Confirm-gated because it is
// destructive. On success it returns to the LOLER list; a 409 (the report has actioned
// items) shows the server's clear message rather than a generic error.
function DeleteReportButton({ reportId }: { reportId: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (user?.role !== "admin") return null;

  async function del() {
    setBusy(true);
    setErr(null);
    try {
      await api.loler.deleteReport(reportId);
      navigate("/office/loler");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not delete the report. Please try again in a moment.");
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setErr(null);
          setConfirming(true);
        }}
      >
        Delete report
      </Button>
      {confirming ? (
        <ModalShell ariaLabel="Delete this LOLER report" onClose={() => !busy && setConfirming(false)} width="max-w-md" panelClassName="p-6">
            <h2 className="mb-2 font-heading text-xl text-ele-strong">Delete this LOLER report?</h2>
            <p className="mb-4 text-sm text-ele-body">
              This removes the report and its defect items. It cannot be undone. The filed report PDF in Drive is left in place.
            </p>
            {err ? <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
            <div className={`gap-2 ${MODAL_ACTIONS}`}>
              <button type="button" disabled={busy} onClick={() => setConfirming(false)} className={`${btnSm} border-ele-purple-accent text-ele-body ${MODAL_ACTION_BTN}`}>
                Cancel
              </button>
              <Button variant="danger" size="sm" disabled={busy} onClick={del} className={MODAL_ACTION_BTN}>
                {busy ? "Deleting..." : "Delete report"}
              </Button>
            </div>
        </ModalShell>
      ) : null}
    </>
  );
}

// Navigate to a fresh quote calculator, prefilled from this report's own recorded specification
// (Session ALO, LOLER spec Item 2): the same fields the Director already reads off a LOLER report
// by hand to build a maintenance quote. Creates NOTHING itself -- the office still reviews, adjusts
// and saves the quote like any other. Reuses the SAME navigation shape the prospect detail page's
// "Generate Quote" already uses (state.prefill), extended with state.link (an existing customer/site,
// when this report is already tied to one) and state.initialLift (one lift's worth of real inputs,
// converted by the calculator's own unitFromStoredLift -- no second mapping here).
function createMaintenanceQuote(navigate: ReturnType<typeof useNavigate>, report: LolerReportDetail) {
  const qp = report.quotePrefill;
  const hasLiftSpec = qp.liftType != null || qp.stops != null || qp.year != null || report.liftSerialNumber != null;
  navigate("/office/quotes/new", {
    state: {
      prefill: {
        customerName: qp.customerName ?? "",
        customerEmail: qp.customerEmail ?? "",
        siteName: qp.siteName ?? "",
        siteAddress: qp.siteAddress ?? "",
      },
      link: { customerId: qp.customerId, siteId: qp.siteId },
      ...(hasLiftSpec
        ? {
            initialLift: {
              // "" rather than omitting: QuoteLiftInput.liftType is required by its type, and
              // unitFromStoredLift treats a blank value exactly as "not given" (its own `if
              // (lift.liftType)` check), so this never fabricates a lift type nothing recognised.
              liftType: qp.liftType ?? "",
              frequency: "quarterly",
              ...(qp.stops != null ? { stops: qp.stops } : {}),
              ...(qp.duration ? { duration: qp.duration as QuoteLiftInput["duration"] } : {}),
              ...(qp.year != null ? { year: qp.year } : {}),
              ...(report.liftSerialNumber ? { serial: report.liftSerialNumber } : {}),
            } satisfies QuoteLiftInput,
          }
        : {}),
    },
  });
}

function ReportHeader({ report, onChange, reload }: { report: LolerReportDetail; onChange: (r: LolerReportDetail) => void; reload: () => Promise<void> }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function setCompleteness(completeness: "full" | "partial") {
    setBusy(true);
    try {
      onChange(await api.loler.updateReport(report.id, { completeness }));
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(file: File) {
    setBusy(true);
    setErr(null);
    try {
      onChange(await api.loler.uploadReportPdf(report.id, file));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not upload the report PDF.");
    } finally {
      setBusy(false);
      void reload();
    }
  }

  return (
    <>
      <RecordHeader
        title={
          report.assetId && report.ele ? (
            <Link to={`/office/lifts/${encodeURIComponent(report.assetId)}`} className="underline-offset-4 transition hover:text-ele-green hover:underline">
              {report.ele}
            </Link>
          ) : (
            (report.ele ?? report.siteName ?? "LOLER report")
          )
        }
        backTo="/office/loler"
        backLabel="Back to reports"
        status={
          <>
            <StatusPill word={`${report.completeness} report`} tone={report.completeness === "full" ? "green" : "amber"} />
            {!report.hasContract ? <StatusPill word="Pre-contract" tone="amber" /> : null}
          </>
        }
        subtitle={
          <>
            {report.siteId ? (
              <Link to={`/office/sites/${encodeURIComponent(report.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                {report.siteName ?? report.siteAddress ?? EMPTY}
              </Link>
            ) : (
              (report.siteName ?? report.siteAddress ?? EMPTY)
            )}
            {report.customerName ? (
              report.customerId ? (
                <>
                  {" | "}
                  <Link to={`/office/customers/${encodeURIComponent(report.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {report.customerName}
                  </Link>
                </>
              ) : (
                ` | ${report.customerName}`
              )
            ) : (
              ""
            )}
          </>
        }
        actions={
          <>
            <button disabled={busy} onClick={() => setCompleteness(report.completeness === "full" ? "partial" : "full")} className={`max-sm:min-h-11 ${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-green`}>
              Mark {report.completeness === "full" ? "partial" : "full"}
            </button>
            {/* Session ALO (LOLER spec Item 2): pre-fills a fresh quote from this report's own recorded
                specification. Never itself creates a quote; the office still reviews, adjusts and
                confirms it on the calculator like any other. Available regardless of completeness or
                whether any lift spec was actually recorded -- customer/site identity alone is still
                worth prefilling (matches the prospect page's own "Generate Quote", which has never
                required one either). */}
            <Button variant="secondary" size="sm" onClick={() => createMaintenanceQuote(navigate, report)}>
              Create maintenance quote
            </Button>
            <DeleteReportButton reportId={report.id} />
          </>
        }
      />

    <div className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      {report.completeness === "partial" ? (
        <p className="mt-3 rounded-ele border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
          This is a partial extract. A remedial quote cannot be finalised until the full report is on file. Prompt the duty holder for the complete document, then mark it full.
        </p>
      ) : null}

      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <div><dt className={labelCls}>Inspector</dt><dd className="text-ele-strong">{report.inspectorName ?? EMPTY}</dd></div>
        <div><dt className={labelCls}>Examination date</dt><dd className="text-ele-strong">{formatDate(report.examinationDate)}</dd></div>
        <div><dt className={labelCls}>Next exam due</dt><dd className="text-ele-strong">{formatDate(report.nextExaminationDue)}</dd></div>
        <div><dt className={labelCls}>Report ref</dt><dd className="text-ele-strong">{report.reportRef ?? EMPTY}</dd></div>
      </dl>

      {/* The lift's own technical specification, as stated on THIS report (Session ALO) -- never
          the asset's own registered fields, which may differ or may not exist yet. Shown only when
          the report actually recorded something, matching every other optional field on this page. */}
      {report.liftType || report.liftSerialNumber || report.liftFloors != null || report.liftInstallYear != null || report.liftAgeYears != null ? (
        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <div><dt className={labelCls}>Lift type (as reported)</dt><dd className="text-ele-strong">{report.liftType ?? EMPTY}</dd></div>
          <div><dt className={labelCls}>Serial / plant number</dt><dd className="text-ele-strong">{report.liftSerialNumber ?? EMPTY}</dd></div>
          <div><dt className={labelCls}>Floors served</dt><dd className="text-ele-strong">{report.liftFloors ?? EMPTY}</dd></div>
          <div>
            <dt className={labelCls}>Age / install year</dt>
            <dd className="text-ele-strong">{report.liftInstallYear != null ? report.liftInstallYear : report.liftAgeYears != null ? `${report.liftAgeYears} years` : EMPTY}</dd>
          </div>
        </dl>
      ) : null}
      {report.quotePrefill.unmappedLiftType ? (
        <p className="mt-2 text-xs text-amber-400">
          The lift type above ("{report.quotePrefill.unmappedLiftType}") was not recognised for pricing. Choose it manually on the quote calculator.
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void onUpload(f); e.target.value = ""; }} />
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
          {report.hasReportPdf ? "Replace report PDF" : "Upload report PDF"}
        </Button>
        {report.hasReportPdf ? (
          <a href={api.loler.reportPdfHref(report.id)} className={`${btnSm} border-ele-purple-accent text-ele-body hover:text-ele-strong`}>View report PDF</a>
        ) : null}
      </div>
      {err ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
    </div>
    </>
  );
}

function ItemsTable({ report, reload }: { report: LolerReportDetail; reload: () => Promise<void> }) {
  if (report.items.length === 0) {
    return <EmptyState className="mt-5" message="No defect items yet. Add the report's items below." />;
  }
  return (
    <div className="mt-5 space-y-3">
      {report.items.map((item) => (
        <DefectCard key={item.id} item={item} canQuote={report.canFinaliseQuote} reload={reload} />
      ))}
    </div>
  );
}

function DefectCard({ item, canQuote, reload }: { item: LolerDefectItem; canQuote: boolean; reload: () => Promise<void> }) {
  const [panel, setPanel] = useState<null | "quote" | "absorb">(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // A defect item logged in error can be removed when it is untouched: still open, no confirmed
  // treatment, no remedial quote (Session AB, Item 6). The server enforces the full guard (incl. a
  // linked job) and returns a plain-English 409 otherwise.
  const canDelete = isAdmin && item.status === "open" && !item.treatment && item.xeroInvoiceId == null;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      setPanel(null);
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? (e.code === "report_partial" ? "Mark the report complete before finalising a remedial quote." : e.message || e.code) : "Action failed.");
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    setBusy(true);
    setErr(null);
    try {
      await api.loler.deleteDefect(item.id);
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not delete the defect item. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const p = item.proposal;

  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill word={CATEGORY_LABEL[item.category]} tone={statusEntry("lolerCategory", item.category).tone} />
            <StatusPill word={item.status.replace("_", " ")} tone="grey" />
            {item.deadline ? <span className={`font-menu text-[11px] ${item.overdue ? "text-ele-danger" : "text-ele-body"}`}>Due {formatDate(item.deadline)}{item.overdue ? " (overdue)" : ""}</span> : null}
          </div>
          <p className="mt-2 text-ele-strong">{item.description}</p>

          {/* The platform proposal (advisory; the human confirms). */}
          <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-base px-3 py-2 text-xs text-ele-body">
            <span className="font-menu uppercase tracking-wide text-ele-blue">Proposed: </span>
            <StatusPill word={p.treatment ?? (p.grey ? "your call" : "classify")} tone={treatmentTone(p.treatment)} />
            {p.honoured ? <StatusPill className="ml-1" word="honoured" tone="green" /> : null}
            {p.leadGenAbsorbCandidate ? <StatusPill className="ml-1" word="absorb to win the contract" tone="amber" /> : null}
            <span className="ml-2">{p.reason}</span>
          </div>

          {/* The confirmed treatment + any money. */}
          {item.treatment ? (
            <p className="mt-2 text-xs text-ele-body">
              <span className="font-menu uppercase tracking-wide">Confirmed: </span>
              <StatusPill word={item.treatment} tone={treatmentTone(item.treatment)} />
              {item.remedialQuoteAmount != null ? <span className="ml-2">Quote {gbp(item.remedialQuoteAmount)} ex VAT</span> : null}
              {item.subcontractCost != null ? <span className="ml-1 text-ele-body">(subcontractor cost {gbp(item.subcontractCost)})</span> : null}
              {item.concessionValue != null ? <span className="ml-2">Concession {gbp(item.concessionValue)}</span> : null}
              {item.xeroInvoiceId ? <a href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${item.xeroInvoiceId}`} target="_blank" rel="noopener noreferrer" className="ml-2 text-ele-green hover:underline">View in Xero</a> : null}
            </p>
          ) : null}
        </div>

        {/* Lifecycle. */}
        <div className="flex shrink-0 flex-col items-end gap-2">
          <select
            value={item.status === "signed_off" ? "signed_off" : item.status}
            disabled={busy || item.status === "signed_off"}
            onChange={(e) => { const v = e.target.value; if (v !== item.status) void run(() => api.loler.updateDefect(item.id, { status: v as LolerDefectLifecycle })); }}
            className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 text-xs disabled:opacity-60"
          >
            {LIFECYCLE.map((s) => <option key={s} value={s}>{LIFECYCLE_LABEL[s]}</option>)}
            {item.status === "signed_off" ? <option value="signed_off">Signed off</option> : null}
          </select>
          {item.status === "signed_off" ? (
            <button disabled={busy} onClick={() => run(() => api.loler.updateDefect(item.id, { signedOff: false }))} className={`${btnSm} border-ele-purple-accent text-ele-body`}>Reopen</button>
          ) : (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => run(() => api.loler.updateDefect(item.id, { signedOff: true }))}>Sign off</Button>
          )}
        </div>
      </div>

      {/* Treatment actions. */}
      <div className="mt-3 flex flex-wrap gap-2 border-t border-ele-purple-accent pt-3">
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => run(() => api.loler.cover(item.id))}>Covered (schedule)</Button>
        <button disabled={busy || !canQuote} title={canQuote ? "" : "Mark the report complete first"} onClick={() => setPanel(panel === "quote" ? null : "quote")} className={`${btnSm} border-amber-400 text-amber-400 hover:bg-ele-inset`}>Chargeable (quote)</button>
        <button disabled={busy} onClick={() => setPanel(panel === "absorb" ? null : "absorb")} className={`${btnSm} border-ele-loler text-ele-loler hover:bg-ele-inset`}>Absorb (concession)</button>
        {canDelete ? (
          <ConfirmButton onConfirm={del} label="Delete item" busy={busy} className={buttonClasses("destructive", "sm", "ml-auto")} armedClassName="!bg-ele-danger-fill !text-white font-semibold" />
        ) : null}
      </div>

      {panel === "quote" ? <QuotePanel busy={busy} onSubmit={(body) => run(() => api.loler.quote(item.id, body))} /> : null}
      {panel === "absorb" ? <AbsorbPanel busy={busy} onSubmit={(body) => run(() => api.loler.absorb(item.id, body))} /> : null}
      {err ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
    </div>
  );
}

function QuotePanel({ busy, onSubmit }: { busy: boolean; onSubmit: (body: RemedialQuoteInput) => void }) {
  const [labour, setLabour] = useState("");
  const [parts, setParts] = useState<{ description: string; amount: string }[]>([]);
  const [subCost, setSubCost] = useState("");
  const [subCharge, setSubCharge] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  function submit() {
    const body: RemedialQuoteInput = { sendEmail };
    if (labour && Number(labour) > 0) body.labour = { amount: Number(labour) };
    const p = parts.filter((x) => x.description.trim() && Number(x.amount) > 0).map((x) => ({ description: x.description.trim(), amount: Number(x.amount) }));
    if (p.length) body.parts = p;
    // Include the subcontract block when EITHER the charge-on (211 line) or the cost
    // (recorded for margin) is entered, so a cost is never silently dropped.
    if ((subCharge && Number(subCharge) > 0) || (subCost && Number(subCost) > 0)) {
      body.subcontract = { chargeOn: subCharge ? Number(subCharge) : 0, cost: subCost ? Number(subCost) : undefined };
    }
    onSubmit(body);
  }

  return (
    <div className="mt-3 rounded-ele border border-amber-500/40 bg-ele-surface p-3">
      <p className="mb-1 font-menu text-xs uppercase tracking-wide text-amber-400">Remedial quote (DRAFT only)</p>
      {/* The nominal account codes are recorded for the office but kept out of the field labels
          (Session AEH, FIX-1 A5). */}
      <p className="mb-2 text-[11px] text-ele-body opacity-70">Nominal codes: labour 207, parts 208, subcontracted 211, subcontractor cost 511 (internal).</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div><label className={labelCls}>Labour (ex VAT)</label><input type="number" min="0" step="0.01" className={inputCls} value={labour} onChange={(e) => setLabour(e.target.value)} /></div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div><label className={labelCls}>Subcontractor cost</label><input type="number" min="0" step="0.01" className={inputCls} value={subCost} onChange={(e) => setSubCost(e.target.value)} placeholder="Internal cost only" /></div>
          <div><label className={labelCls}>Charge on</label><input type="number" min="0" step="0.01" className={inputCls} value={subCharge} onChange={(e) => setSubCharge(e.target.value)} /></div>
        </div>
      </div>
      <div className="mt-2">
        <div className="mb-1 flex items-center justify-between">
          <label className={labelCls + " mb-0"}>Parts</label>
          <button onClick={() => setParts((a) => [...a, { description: "", amount: "" }])} className="font-menu text-xs text-ele-green hover:underline">+ Add part</button>
        </div>
        {parts.map((p, i) => (
          <div key={i} className="mb-1 grid grid-cols-[1fr_120px_auto] gap-2">
            <input className={inputCls} placeholder="Part description" value={p.description} onChange={(e) => setParts((a) => a.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
            <input type="number" min="0" step="0.01" className={inputCls} placeholder="Amount" value={p.amount} onChange={(e) => setParts((a) => a.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} />
            <button onClick={() => setParts((a) => a.filter((_, j) => j !== i))} className="rounded-ele border border-ele-purple-accent px-2 text-ele-body hover:text-ele-danger">x</button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-ele-body"><input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} /> Email the quote to the customer</label>
        <button disabled={busy} onClick={submit} className="rounded-ele bg-amber-400 px-4 py-2 font-heading text-xs tracking-wide text-ele-bg disabled:opacity-50">Raise remedial quote</button>
      </div>
    </div>
  );
}

function AbsorbPanel({ busy, onSubmit }: { busy: boolean; onSubmit: (body: { note?: string; value?: number }) => void }) {
  const [note, setNote] = useState("");
  const [value, setValue] = useState("");
  return (
    <div className="mt-3 rounded-ele border border-ele-loler bg-ele-surface p-3">
      <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-loler">Absorb as a concession (no charge). Recorded internally and honoured on the contract.</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px_auto]">
        <input className={inputCls} placeholder="Goodwill note (internal)" value={note} onChange={(e) => setNote(e.target.value)} />
        <input type="number" min="0" step="0.01" className={inputCls} placeholder="Value given away" value={value} onChange={(e) => setValue(e.target.value)} />
        <button disabled={busy} onClick={() => onSubmit({ note: note || undefined, value: value ? Number(value) : undefined })} className="rounded-ele bg-ele-loler px-4 py-2 font-heading text-xs tracking-wide text-ele-bg disabled:opacity-50">Record concession</button>
      </div>
    </div>
  );
}

function AddDefect({ reportId, reload }: { reportId: string; reload: () => Promise<void> }) {
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<LolerDefectCategory>("advisory");
  const [deadline, setDeadline] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    if (!description.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.loler.addDefect(reportId, { description: description.trim(), category, deadline: deadline || null });
      setDescription("");
      setDeadline("");
      setCategory("advisory");
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not add the defect.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <h3 className="mb-2 font-heading text-lg tracking-wide">Add a defect</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_180px_150px_auto]">
        <input className={inputCls} placeholder="Defect description (from the report)" value={description} onChange={(e) => setDescription(e.target.value)} />
        <select className={inputCls} value={category} onChange={(e) => setCategory(e.target.value as LolerDefectCategory)}>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input type="date" className={inputCls} value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        <Button disabled={busy} onClick={add}>Add</Button>
      </div>
      {err ? <p role="alert" className="mt-2 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">{err}</p> : null}
    </div>
  );
}
